import { RadioGroup } from '@mui/material';
import { type VoidFunctionComponent } from 'react';

import { LoaderButton } from '../../../generic/Button';
import CopyPreformattedToClipboard from '../../../generic/CopyPreformattedToClipboard';
import { LabeledRadio, LabeledTextInput } from '../../../generic/Forms';
import { DocsLink, RouterLink } from '../../../generic/Link';
import Text from '../../../generic/Text';

import { UPLOAD_STEPS } from '.';
import useAPITokenStep from './useAPITokenStep';
import type { CLIUploadGuidePlatform } from '.';
import type { APITokenStep } from './useAPITokenStep';

import styles from './APITokenStep.module.less';

type Props = {
  platform: CLIUploadGuidePlatform;
};

/**
 * CLIUploadGuideAPITokenStep is the part of the CLI upload guide which
 * instructs the user to set their API key. They have the ability to either
 * use an existing API token or create a new one inline. When creating
 * the API token, the user is allowed to provide a name and nothing else.
 * The created API token will always be a push-only token.
 */
const CLIUploadGuideAPITokenStep: VoidFunctionComponent<Props> = ({ platform }) => {
  const {
    apiTokenStep,
    hasAPIToken,
    setApiTokenStep,
    tokenName,
    onTokenNameChange,
    token,
    isCreatingToken,
    onCreateNewAPIToken,
    tokenOrFallbackText,
  } = useAPITokenStep();

  return (
    <>
      {/* Using a Fragment here because the CopyPreformattedToClipboard needs to take up the full width of the container */}
      <div className="padded padded-small">
        <Text.H4>
          2. Set your API key
        </Text.H4>
        <Text.Paragraph>
          Set your API key. You can create and manage API keys
          {' '}
          <RouterLink to="/account/settings/integrations/api_tokens">
            here
          </RouterLink>
          .
        </Text.Paragraph>
        <RadioGroup
          name="propagateDefaultReportCustomText"
          defaultValue={apiTokenStep}
          onChange={(_event, value: APITokenStep) => setApiTokenStep(value)}
          row
        >
          <LabeledRadio
            label="Create API Token"
            value="create"
          />
          <LabeledRadio
            label="I have an API Token"
            value="existing"
          />
        </RadioGroup>
        {!hasAPIToken && token === null && (
          <div className={styles.createToken}>
            <Text.H4>
              Create an API token
            </Text.H4>
            <div className="width-full">
              <LabeledTextInput
                label="Name"
                value={tokenName}
                onChange={onTokenNameChange}
                placeholder="My API key token"
              />
              <Text.Paragraph>
                Give the token a name to help identify it later. This token is push only.
                {' '}
                <DocsLink to="api-reference#api-tokens" openInNewTab>
                  Learn more
                </DocsLink>
                .
              </Text.Paragraph>
            </div>
            <LoaderButton
              type="primary"
              size="large"
              isDisabled={!tokenName}
              isLoading={isCreatingToken}
              onClick={onCreateNewAPIToken}
            >
              Generate API Token
            </LoaderButton>
          </div>
        )}
        {!hasAPIToken && token !== null && (
          <div>
            <Text.H4>
              Token successfully generated
            </Text.H4>
            <Text.Paragraph>
              Please copy this token and store it safely for future use.
              {' '}
              <span className="bold">
                It will never be displayed again and is not retrievable.
              </span>
            </Text.Paragraph>
          </div>
        )}
      </div>
      {(hasAPIToken || token !== null) && <CopyPreformattedToClipboard text={`${UPLOAD_STEPS[platform].API_KEY}${tokenOrFallbackText}`} />}
    </>
  );
};

export default CLIUploadGuideAPITokenStep;
